
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  Calendar,
  BarChart3,
  AlertCircle,
  Target,
  RefreshCw
} from "lucide-react";
import { format, addMonths } from "date-fns";

interface RevenueForecast {
  month: string;
  projected: number;
  actual?: number;
  confidence: 'high' | 'medium' | 'low';
  factors: string[];
}

interface ClientRevenueForecastTabProps {
  clientId: string;
  clientName: string;
}

// Mock forecast data
const mockForecastData: RevenueForecast[] = [
  {
    month: format(new Date(), 'MMM yyyy'),
    projected: 85000,
    actual: 92000,
    confidence: 'high',
    factors: ['Completed placement', 'Retainer payment']
  },
  {
    month: format(addMonths(new Date(), 1), 'MMM yyyy'),
    projected: 120000,
    confidence: 'high',
    factors: ['3 active placements', 'Contract renewal']
  },
  {
    month: format(addMonths(new Date(), 2), 'MMM yyyy'),
    projected: 95000,
    confidence: 'medium',
    factors: ['2 pending offers', 'Seasonal adjustment']
  },
  {
    month: format(addMonths(new Date(), 3), 'MMM yyyy'),
    projected: 110000,
    confidence: 'medium',
    factors: ['Pipeline development', 'Market growth']
  }
];

export const ClientRevenueForecastTab: React.FC<ClientRevenueForecastTabProps> = ({ 
  clientId, 
  clientName 
}) => {
  const [forecastData] = useState<RevenueForecast[]>(mockForecastData);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const getConfidenceColor = (confidence: string) => {
    switch (confidence) {
      case 'high': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTrendIcon = (current: number, previous: number) => {
    if (current > previous) {
      return <TrendingUp className="h-4 w-4 text-green-600" />;
    } else if (current < previous) {
      return <TrendingDown className="h-4 w-4 text-red-600" />;
    }
    return <BarChart3 className="h-4 w-4 text-gray-600" />;
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleRefreshForecast = async () => {
    setIsRefreshing(true);
    // Simulate API call
    setTimeout(() => {
      setIsRefreshing(false);
    }, 2000);
  };

  const totalProjected = forecastData.reduce((sum, item) => sum + item.projected, 0);
  const currentMonth = forecastData[0];
  const nextMonth = forecastData[1];

  return (
    <div className="space-y-6">
      {/* Forecast Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Current Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(currentMonth.projected)}</div>
            {currentMonth.actual && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                {getTrendIcon(currentMonth.actual, currentMonth.projected)}
                <span>Actual: {formatCurrency(currentMonth.actual)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Next Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(nextMonth.projected)}</div>
            <Badge className={getConfidenceColor(nextMonth.confidence)}>
              {nextMonth.confidence} confidence
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Quarterly Forecast</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalProjected)}</div>
            <p className="text-xs text-muted-foreground">4-month projection</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Forecast Accuracy</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">92%</div>
            <p className="text-xs text-muted-foreground">Last 6 months avg</p>
          </CardContent>
        </Card>
      </div>

      {/* Forecast Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Target className="h-5 w-5" />
            Forecast Management
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button 
              className="h-16 flex flex-col items-center gap-2"
              onClick={handleRefreshForecast}
              disabled={isRefreshing}
            >
              <RefreshCw className={`h-5 w-5 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="text-sm">Refresh Forecast</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              <span className="text-sm">View Analytics</span>
            </Button>
            <Button variant="outline" className="h-16 flex flex-col items-center gap-2">
              <Calendar className="h-5 w-5" />
              <span className="text-sm">Set Targets</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Forecast Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Revenue Forecast Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {forecastData.map((forecast, index) => (
              <div key={forecast.month} className="border rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <h3 className="font-medium">{forecast.month}</h3>
                    <Badge className={getConfidenceColor(forecast.confidence)}>
                      {forecast.confidence} confidence
                    </Badge>
                  </div>
                  <div className="text-right">
                    <div className="text-lg font-semibold">{formatCurrency(forecast.projected)}</div>
                    {forecast.actual && (
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        {getTrendIcon(forecast.actual, forecast.projected)}
                        <span>Actual: {formatCurrency(forecast.actual)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {forecast.actual && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Forecast Accuracy</span>
                      <span>{Math.round((Math.min(forecast.actual, forecast.projected) / Math.max(forecast.actual, forecast.projected)) * 100)}%</span>
                    </div>
                    <Progress 
                      value={(Math.min(forecast.actual, forecast.projected) / Math.max(forecast.actual, forecast.projected)) * 100} 
                      className="h-2"
                    />
                  </div>
                )}

                <div>
                  <p className="text-sm font-medium mb-2">Key Factors:</p>
                  <div className="flex flex-wrap gap-2">
                    {forecast.factors.map((factor, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs">
                        {factor}
                      </Badge>
                    ))}
                  </div>
                </div>

                {index < forecastData.length - 1 && forecast.confidence === 'low' && (
                  <div className="flex items-start gap-2 p-2 bg-yellow-50 rounded border-l-4 border-yellow-400">
                    <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5" />
                    <div className="text-sm">
                      <p className="font-medium text-yellow-800">Low Confidence Alert</p>
                      <p className="text-yellow-700">Consider reviewing pipeline and market factors for more accurate forecasting.</p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
